export interface CoreCodexSkill {
    id: string;
    title: string;
    category: string;
    status: string;
    summary: string;
    bestFor: string;
    avoid: string;
    output: string;
    href: string;
    sourceLabel?: string;
    sourceHref?: string;
    tags: string[];
}

export interface CoreCodexSkillGroup {
    id: string;
    order: string;
    title: string;
    summary: string;
    skills: CoreCodexSkill[];
}

export const CORE_CODEX_SKILL_GROUPS: CoreCodexSkillGroup[] = [
    {
        id: 'control',
        order: '01',
        title: '작업 제어',
        summary: '긴 작업에서 목표, 범위, 중단 조건을 흔들리지 않게 잡는 스킬입니다.',
        skills: [
            {
                id: 'goal',
                title: 'Codex /goal',
                category: '목표 관리',
                status: '상세 문서 있음',
                summary: '긴 작업을 세션 목표로 고정하고 완료 조건까지 추적하는 스킬입니다.',
                bestFor: '리팩터링, 마이그레이션, CI 복구처럼 30분 이상 이어지고 검증 명령이 있는 작업',
                avoid: '한 번 읽고 답하면 끝나는 짧은 질문이나 범위가 아직 정해지지 않은 아이디어 탐색',
                output: '목표, 수정 범위, 검증 명령, 중단 조건, 완료 판정',
                href: '/codex-skills/goal',
                tags: ['goal', 'long running', 'verification'],
            },
            {
                id: 'skill-router',
                title: 'Skill Router',
                category: '스킬 선택',
                status: '핵심 후보',
                summary: '여러 스킬 중 지금 작업에 필요한 것만 고르고, 쓰지 말아야 할 조건도 함께 판단합니다.',
                bestFor: '조사, 구현, 리뷰, 압축처럼 성격이 다른 요청이 한 세션에 섞이는 ReviewOps 작업',
                avoid: '항상 모든 스킬을 읽어 컨텍스트를 낭비하는 방식',
                output: '선택한 스킬, 선택 이유, 비사용 스킬, 필요한 참조 파일',
                href: '/codex-skills#skill-router',
                tags: ['routing', 'metadata', 'selection'],
            },
        ],
    },
    {
        id: 'knowledge',
        order: '02',
        title: '조사·지식화',
        summary: '공식 출처, 웹 검색 결과, 다음 조사 항목을 남겨 지식을 단계적으로 쌓는 스킬입니다.',
        skills: [
            {
                id: 'research-brief',
                title: 'Research Brief',
                category: '공식 출처 조사',
                status: '핵심 후보',
                summary: '공식 문서와 기술 블로그를 짧은 위키 항목으로 압축하고 다음 조사 후보를 남깁니다.',
                bestFor: 'OpenAI 기능, 벤치마크, API 변화처럼 최신성·출처가 중요한 조사',
                avoid: '출처가 필요 없는 내부 의견 정리나 이미 고정된 사내 정책 문서',
                output: '핵심 요약, 사용/미사용 차이, 공식 링크, 다음 조사 항목',
                href: '/codex-skills#research-brief',
                tags: ['research', 'official source', 'wiki'],
            },
            {
                id: 'web-search',
                title: 'Web Search Skill',
                category: '웹 검색',
                status: '프로젝트 설치됨',
                summary: 'Brave Search API 기반 SKILL.md를 프로젝트 .agents/skills/web-search에 설치해 Codex CLI가 검색 결과, 스니펫, freshness 필터를 재사용할 수 있게 한 스킬입니다.',
                bestFor: 'BRAVE_SEARCH_API_KEY가 준비된 환경에서 최신 자료, URL 목록, 스니펫, 날짜 필터가 필요한 리서치',
                avoid: 'API 키가 없거나 Codex CLI의 기본 web_search cached/live 모드만으로 충분한 단순 확인',
                output: 'Brave Search API 호출 예시, ranked result JSON, freshness/safesearch/goggles 파라미터',
                href: '/codex-skills#web-search',
                sourceLabel: 'GitHub 원본',
                sourceHref: 'https://github.com/brave/brave-search-skills/tree/main/skills/web-search',
                tags: ['web search', 'brave', 'github', 'freshness'],
            },
        ],
    },
    {
        id: 'delivery',
        order: '03',
        title: '구현·검증',
        summary: '파일 수정, 테스트, 리뷰 대응처럼 실제 산출물을 안전하게 끝내는 스킬입니다.',
        skills: [
            {
                id: 'patch-verify',
                title: 'Patch & Verify',
                category: '구현 루프',
                status: '핵심 후보',
                summary: '요청을 파일 변경으로 좁히고 빌드·테스트·잔여 리스크까지 한 번에 정리합니다.',
                bestFor: '프론트엔드 화면 수정, 라우트 추가, 데이터 카탈로그 갱신처럼 검증 가능한 코드 변경',
                avoid: '사용자 확인 없이 큰 아키텍처를 갈아엎어야 하는 작업',
                output: '변경 파일, 검증 명령 결과, 실패 시 원인, 남은 리스크',
                href: '/codex-skills#patch-verify',
                tags: ['implementation', 'test', 'build'],
            },
            {
                id: 'review-pass',
                title: 'Review Pass',
                category: '품질 리뷰',
                status: '핵심 후보',
                summary: '리뷰 코멘트나 위험 신호를 버그, 회귀, 테스트 공백 중심으로 처리합니다.',
                bestFor: 'ReviewOps 피드백, PR 리뷰, QA 코멘트를 실제 수정 항목으로 바꾸는 작업',
                avoid: '취향 차이에 가까운 문구·색상 의견을 근거 없이 확대하는 작업',
                output: '우선순위, 수정 내용, 확인한 회귀, 남은 질문',
                href: '/codex-skills#review-pass',
                tags: ['review', 'qa', 'risk'],
            },
        ],
    },
];

export const CORE_CODEX_SKILLS: CoreCodexSkill[] = CORE_CODEX_SKILL_GROUPS.reduce(
    (skills, group) => skills.concat(group.skills),
    [] as CoreCodexSkill[],
);
